import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/Select';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Label } from '@/components/ui/Label';
import { useAuth } from '@/contexts/AuthContext';
import { SearchFilter } from '@/services/ProductsService';

interface SearchProductsFilterProps {
  searchFilters: Partial<SearchFilter>;
  onSearchFilters: <K extends keyof SearchFilter>(
    filterKey: K,
    filterValue: SearchFilter[K] | undefined,
  ) => void;
}

export function SearchProductsFilter({
  searchFilters,
  onSearchFilters,
}: SearchProductsFilterProps) {
  const [showFilters, setShowFilters] = useState<boolean>(false);

  const [searchFiltersCount, setSearchFilterCount] = useState(0);

  const { t } = useTranslation('translation', {
    keyPrefix: 'components.SearchProductsFilter',
  });

  const { user } = useAuth();

  const visionCategoriesAvailable =
    typeof user?.customer?.visionCategoriesAvailable === 'string'
      ? user?.customer.visionCategoriesAvailable
          .split(',')
          .map((v) => v.trim())
          .sort()
      : [];

  function handleClearFilters() {
    onSearchFilters('pairsWith', undefined);
    onSearchFilters('availableProduct', undefined);
    onSearchFilters('visionCategory', undefined);
  }

  useEffect(() => {
    const searchFiltersKeys: (keyof SearchFilter)[] = [
      'pairsWith',
      'availableProduct',
      'visionCategory',
    ];

    const count = searchFiltersKeys.reduce((acc, key) => {
      return acc + (searchFilters[key] ? 1 : 0);
    }, 0);

    setSearchFilterCount(count);
  }, [searchFilters]);

  return (
    <div className="mb-6">
      <Button
        variant="outline"
        onClick={() => setShowFilters(!showFilters)}
        className="flex items-center gap-2"
      >
        {showFilters ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
        {t('showButtonLabel')}
        {searchFiltersCount > 0 && (
          <Badge variant="secondary" className="ml-2">
            {searchFiltersCount}
          </Badge>
        )}
      </Button>
      <div className="mt-4 flex items-center gap-4">
        {searchFiltersCount > 0 && (
          <Badge variant="secondary">
            {t('activeFilterBadge', { count: searchFiltersCount })}
          </Badge>
        )}

        {searchFiltersCount > 0 && (
          <Button variant="outline" size="sm" onClick={handleClearFilters}>
            <X className="mr-1 h-4 w-4" />
            {t('clear')}
          </Button>
        )}
      </div>

      {showFilters && (
        <div className="mt-6 space-y-2">
          {visionCategoriesAvailable.length > 0 && (
            <div className="max-w-[200px] rounded-lg bg-white">
              <h3 className="mb-3 text-sm font-medium text-gray-700">
                {t('category')}
              </h3>

              <Select
                value={searchFilters.visionCategory}
                onValueChange={(value) =>
                  onSearchFilters('visionCategory', value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('categorySelectPlaceholder')} />
                </SelectTrigger>

                <SelectContent>
                  {visionCategoriesAvailable.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex flex-row flex-wrap rounded-lg bg-white p-4 shadow-sm">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div>
                <h3 className="mb-3 text-sm font-medium text-gray-700">
                  {t('features')}
                </h3>

                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="pairsWith"
                      checked={!!searchFilters.pairsWith}
                      onCheckedChange={(checked: boolean) => {
                        onSearchFilters('pairsWith', checked || undefined);
                      }}
                    />

                    <Label htmlFor="pairsWith" className="text-sm">
                      {t('filters.pairsWith')}
                    </Label>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="mb-3 text-sm font-medium text-gray-700">
                  {t('status')}
                </h3>

                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="availableProduct"
                      checked={!!searchFilters.availableProduct}
                      onCheckedChange={(checked: boolean) => {
                        onSearchFilters(
                          'availableProduct',
                          checked || undefined,
                        );
                      }}
                    />
                    <Label htmlFor="availableProduct" className="text-sm">
                      {t('filters.availableProduct')}
                    </Label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
